import { Component, OnInit, Input, OnChanges, Output, EventEmitter, SimpleChanges, HostListener, ElementRef, forwardRef, OnDestroy, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { Validators, FormBuilder, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router } from '@angular/router';
import { set, orderBy, groupBy, cloneDeep } from 'lodash';
import { animate, style, transition, trigger } from '@angular/animations';
import {
  ActionBehavior, ActionResponse, ActionView, BreadcrumbOption, ButtonComponentConfig, ButtonType, ComponentSize, CrudFormButtonItem,
  CrudFormConfig, CrudFormConfigInputChangeReaction,
  CrudFormItem, CrudFormItemMenuItem, CrudFormLabelFunctionItem, CrudFormObjectItem, CrudFormValueEmitMode, CrudViewFormItemType,
  EntitySelectionConfig, FormLayout, IconComponentConfig,
  IconPlacement, IconType, MustMatch
} from 'libs/components/src/lib/utils';
import { CrudFormItemOutputEvent } from 'libs/components/src/lib/utils/edit-form/crud-form-item-output-event.interface';
import { FormService } from 'libs/components/src/lib/services';
import { moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
  selector: 'rappider-form-builder-renderer',
  templateUrl: './form-builder-renderer.component.html',
  styleUrls: ['./form-builder-renderer.component.scss'],
  animations: [
    trigger(
      'inOutAnimation',
      [
        transition(
          ':enter',
          [
            style({ transform: 'translateY(100%)', opacity: 0, position: 'absolute' }),
            animate('200ms', style({ transform: 'translateY(0)', opacity: 1 }))
          ]
        ),
        transition(
          ':leave',
          [
            style({ transform: 'translateY(0)', opacity: 1 }),
            animate('200ms', style({ transform: 'translateY(-100%)', opacity: 0 }))
          ]
        )
      ]
    )
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => FormBuilderRendererComponent),
      multi: true
    }
  ]
})
export class FormBuilderRendererComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

  /* configuration of this component */
  @Input() config: CrudFormConfig;
  /* data for default input values */
  @Input() data: any;
  /* auto submit&dirty value for form */
  @Input() submitted: boolean;
  /* loading status for submit button */
  @Input() submitButtonLoading: boolean;
  /* dynamic data for select box */
  @Input() dynamicDataForSelectBox: EntitySelectionConfig[];
  /* number of menu items, Dropdown if greater than 2, otherwise button */
  @Input() itemLength = 2; // default number is 2
  @Input() slideMode = false;
  @Input() stepTagVisible = false;
  @Input() dividerMode = false;
  @Input() showErrors: boolean;
  @Input() focusFirstInput = true;
  @Input() selectedItem;
  @Input() scrollToLastItem: boolean;
  @Input() autoSelectedFormItem: string;

  @Output() formSubmit = new EventEmitter<any>();
  @Output() submitButtonClick = new EventEmitter<any>();
  @Output() cancelButtonClick = new EventEmitter<boolean>();
  @Output() formValueChange = new EventEmitter<any>();
  @Output() fieldValueChange = new EventEmitter<any>();
  @Output() validityChange = new EventEmitter<boolean>();
  @Output() buttonClick = new EventEmitter<ActionResponse>();
  @Output() labelMenuItemClick = new EventEmitter<CrudFormItemMenuItem>();
  @Output() fieldLinkStateChange = new EventEmitter<CrudFormItem>();
  /* You can use this output to route component events. ex: (close)="onItemOutputEvent('close', $event)" */
  @Output() itemOutputEvent = new EventEmitter<CrudFormItemOutputEvent>();
  /* use select button output */
  @Output() selectButtonClick = new EventEmitter<ButtonComponentConfig>();
  @Output() objectModalOpen = new EventEmitter<any>();
  @Output() iconClick = new EventEmitter();
  @Output() selectItem = new EventEmitter();
  @Output() scrollToLastItemEmit = new EventEmitter<boolean>();

  _value: any[];

  /* form variables */
  dataForm: FormGroup;
  dataFormFieldErrors: { [key: string]: string[] } = {};
  isDataFormFieldErrorsChecked = false;
  disableEmitFieldChange = false;

  loading = true;

  objectFieldEditModal: {
    visible: boolean;
    breadcrumbOptions: BreadcrumbOption[];
    activeBreadcrumbOption: BreadcrumbOption;
  } = {
      visible: false,
      breadcrumbOptions: [],
      activeBreadcrumbOption: null
    };

  /* DEFAULT DEFINITIONS */
  FULL_COMPONENT_SIZE: ComponentSize = { xs: 24 };
  DEFAULT_LABEL_COMPONENT_SIZE: ComponentSize = { xs: 24, sm: 24, md: 6, lg: 5, xxl: 5 };
  DEFAULT_INPUT_COMPONENT_SIZE: ComponentSize = { xs: 24, sm: 24, md: 18, lg: 19, xxl: 19 };
  DEFAULT_SUBMIT_BUTTON_TEXT = 'Save';
  DEFAULT_FORM_LAYOUT = FormLayout.Horizontal;

  FormLayout = FormLayout;
  ComponentTypes = CrudViewFormItemType;
  ActionBehavior = ActionBehavior;
  ActionView = ActionView;

  /* Form items groupped by sections */
  grouppedItems;
  /* names of the sections */
  itemSections: string[];
  rowNumbers: number[];
  uniqueRowNumbers: number[];

  /**
   * sets true after 500ms
   * used to render the codemirror component properly as codemirror gutter doesn't initialized as it should be.
   *
   * @memberof RappiderEditFormComponent
   */
  timeoutCompleted = false;
  lockIcon: IconComponentConfig = {
    name: 'fa-regular fa-lock-keyhole',
    type: IconType.FontAwesome
  };
  unlockIcon: IconComponentConfig = {
    name: 'fa-regular fa-lock-keyhole-open',
    type: IconType.FontAwesome
  };
  deleteButtonConfig = {
    icon: {
      type: 'FONT_AWESOME',
      name: 'fa-sharp fa-solid fa-trash',
    },
    customColorSettings: {
      color: 'var(--text-color)',
      backgroundColor: '#3aa0ff'
    },
    borderSettings: {
      borderColor: 'transparent',
      borderRadius: '4px 4px 0 0'
    },
  };

  /* This variable is used to switch the visibility of the Monaco editors on the page as we need to wait Monaco js code to complete the initializing process to switch the visibility*/
  monacoEditorLoadTime = 5000;
  monacoCodeEditorVisibility = false;

  activeSlide = 0;
  slideButtonConfig: ButtonComponentConfig = {
    text: 'Ok',
    type: ButtonType.Primary,
    icon: {
      name: 'fal fa-arrow-turn-down-left',
      color: '#fff'
    },
    iconPlacement: IconPlacement.Right
  };

  @HostListener('window:keydown', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (
      this.slideMode
      && event.key === 'Enter'
      && !event.metaKey
      && !event.altKey
      && !event.shiftKey
      && !event.ctrlKey
    ) {
      this.handleMoveNextSlideWithEnter();
    }
  }

  constructor(
    private formBuilder: FormBuilder,
    private formService: FormService,
    private router: Router,
    private elRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) { }

  get value() {
    return this._value;
  }

  set value(value: any[]) {
    this._value = value;
    this.onChange(value);
    this.onTouched();
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  writeValue(value: any[]) {
    if (!value) {
    } else {
      this._value = value;
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  ngOnInit() {
    this.config = cloneDeep(this.config);
    this.generateForm();
    this.initTimeout();
    if (this.showErrors) {
      this.showFormErrorsInitially();
    }
    setTimeout(() => {
      this.monacoCodeEditorVisibility = true;
    }, this.monacoEditorLoadTime);
  }

  ngAfterViewInit(): void {
    if (this.focusFirstInput) {
      this.applyAutofocusToFirstInput();
    }
  }

  applyAutofocusToFirstInput() {
    const formInputs = this.elRef.nativeElement.querySelectorAll('input');
    if (formInputs.length > 0) {
      const firstInput = formInputs[0];
      firstInput.focus();

      const value = firstInput.value;
      if (value) {
        firstInput.setSelectionRange(value.length, value.length);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.config?.currentValue) {
      this.config = cloneDeep(this.config);
      this.transformValidators(this.config);
      /* generate form */
      this.generateForm();
      /* set input change reaction if not exist */
      if (!this.config.inputChangeReaction) {
        this.config.inputChangeReaction = CrudFormConfigInputChangeReaction.Default;
      }
      // Only check form errors if dataForm is initialized
      if (this.dataForm) {
        this.checkAllDataFormFieldErrors();
        this.emitValidity();
      }
    }

    if (changes.scrollToLastItem) {
      if (this.scrollToLastItem) {
        this.scrollToBottom();
      }
    }

    if (changes.autoSelectedFormItem) {
      if (this.autoSelectedFormItem) {
        this.selectedItemFieldName(this.autoSelectedFormItem);
      }
    }

    if (changes.data && this.dataForm) {
      // we don't want to trigger value change action when passing data from the parent component,
      // so we use this flag to avoid value change action
      this.disableEmitFieldChange = true;
      this.dataForm.patchValue(this.data);
      this.config.items?.forEach(formItem => {
        this.dataForm?.get(formItem.fieldName)?.updateValueAndValidity({ onlySelf: true });
        this.checkDataFormFieldErrorsByFormItem(formItem);
      });
      this.emitValidity();
      this.disableEmitFieldChange = false;
    }
    if (changes.submitted) {
      this.checkAllDataFormFieldErrors();
      this.emitValidity();
    }
  }

  ngOnDestroy(): void {
    this.selectItem.emit(null);
    this.monacoCodeEditorVisibility = false;
  }

  /*
   * This function is avoids unnecessary render in ngFor loop
   */
  formItemTrackBy(index, formItem) {
    return formItem.fieldName;
  }

  /**
   * This function helps to user to jump next slide when keydown only EnterKey without any key (like metaKey, altKey etc.)
   *
   */
  handleMoveNextSlideWithEnter() {
    const activeFormItem = this.visibleFormItems?.[this.activeSlide];

    if (activeFormItem) {
      if (this.activeSlide === this.visibleFormItems.length - 1) {
        this.onFormSubmit();
      } else {
        this.slideModeOkButtonClick();
        this.focusToActiveSlideInput();
      }
    }
  }

  initTimeout() {
    setTimeout(() => this.timeoutCompleted = true, 500);
  }

  showFormErrorsInitially() {
    this.checkAllDataFormFieldErrors();
  }

  generateForm() {
    this.loading = true;

    if (!this.config?.items) {
      this.loading = false;
      return;
    }

    this.processFormItems();
    this.setupFormGroups();
    this.loading = false;
  }

  /* Display section header if it has a section title and it is not undefined */
  displaySectionHeader(itemSection) {
    return itemSection && itemSection !== 'undefined' && itemSection !== 'Main Section';
  }

  /**
   * returns specified form field's value
   *
   * @param {string} fieldName
   * @returns
   * @memberof EditFormComponent
   */
  getFormFieldValueByFieldName(fieldName: string) {
    return this.dataForm.get(fieldName)?.value;
  }

  createFormGroupByCrudFormItems(crudFormItems: CrudFormItem[], data = null) {
    if (crudFormItems?.length) {
      const formData = {};
      crudFormItems?.forEach(item => {
        if (!item['items']?.length) {
          // #region CREATE FORM FIELD
          if (item.fieldName) {
            /* get initial value */
            const value = (data && data[item.fieldName]) ?? item.default;
            /* get form field validators */
            const validators = item.validators?.filter(validator => validator.type).map(validator => validator.type) || [];
            /* set form field */
            formData[item.fieldName] = [value, validators];
          }
          // #endregion
        } else {
          // #region CREATE FORM GROUP
          /* get nested data */
          const nestedData = data && data[item.fieldName];
          /* get form group */
          formData[item.fieldName] = this.createFormGroupByCrudFormItems(item['items'], nestedData);
          // #endregion
        }
      });
      return this.formBuilder.group(formData);
    }
  }

  /* TODO: move validator functions to validator service */
  generateFormGroupValidators() {
    const validators = [];
    this.config?.validators?.forEach(validator => {
      switch (validator?.validatorKey) {
        case 'mustMatch':
          if (validator.params && validator.params.field1 && validator.params.field2) {
            validators.push(MustMatch(validator.params.field1, validator.params.field2));
          }
          break;
        default:
          break;
      }
    });
    return validators;
  }

  resetForm() {
    this.dataForm.reset();
    this.clearAllDataFormFieldErrors();
  }

  getFieldErrorMessages(form: FormGroup, item: CrudFormItem) {
    let errors = [];
    const errorKeys = this.formService.getActiveErrorKeys(form, item.fieldName);
    if (errorKeys.length) {
      errors = item.validators.map(data => ({ ...data, errorKey: data.errorKey ? data.errorKey.toLowerCase() : data.errorKey })).filter(validator => errorKeys.map(errorKey=>errorKey.toLowerCase()).includes(validator.errorKey) || validator.errorKey === undefined).map(validator => validator.errorMessage);
    }
    return errors;
  }

  checkDataFormFieldErrorsByFormItem(item: CrudFormItem) {
    if (item.fieldName && this.dataForm) {
      const errors = this.getFieldErrorMessages(this.dataForm, item);
      if (errors?.length) {
        this.dataFormFieldErrors[item.fieldName] = errors;
      } else {
        delete this.dataFormFieldErrors[item.fieldName];
      }
    }
  }

  checkAllDataFormFieldErrors() {
    if (!this.isDataFormFieldErrorsChecked && this.dataForm) {
      this.formService.checkFormValidation(this.dataForm);
      this.config.items?.forEach(item => this.checkDataFormFieldErrorsByFormItem(item));
    } else {
      this.isDataFormFieldErrorsChecked = true;
    }
  }

  clearAllDataFormFieldErrors() {
    this.dataFormFieldErrors = {};
    this.isDataFormFieldErrorsChecked = false;
  }

  onButtonItemClick(item: CrudFormButtonItem) {
    if (item.behavior === ActionBehavior.Route) {
      /* navigate */
      this.router.navigateByUrl(item.redirectUrl);
    } else if (item.behavior === ActionBehavior.Emit) {
      /* emit */
      const actionResponse: ActionResponse = {
        action: item
      };
      this.buttonClick.emit(actionResponse);
    }
  }

  onObjectItemButtonClick(item: any) {
    this.objectModalOpen.emit(item);
  }

  onLabelMenuItemClick(menuItem: CrudFormItemMenuItem) {
    this.labelMenuItemClick.emit(menuItem);
  }

  onSelectButtonClick(event: ButtonComponentConfig) {
    this.selectButtonClick.emit(event);
  }

  onFieldValueChange(item: CrudFormItem, value: any) {
    if (!this.disableEmitFieldChange) {
      this.findAndPatchLinkedFormItems(item);
      if (this.config.inputChangeReaction === CrudFormConfigInputChangeReaction.Default) {
        /* set error for changed field */
        this.checkDataFormFieldErrorsByFormItem(item);
        /* emit valid status of the form */
        this.emitValidity();
        /* get changed field body */
        const changedFieldBody = this.getChangedFieldValueBody(item.fieldName, value);
        /* emit */
        this.emitFieldValue(changedFieldBody);
      }
    }
  }

  get visibleFormItems() {
    return this.config.items.filter(item => item.visible !== false);
  }

  // #region LINKED FORM ITEMS

  /* It equates the values of the source item to the target item,
  if there is a data transformationfunction,
  it puts the values into the function and syncs them. */
  findAndPatchLinkedFormItems(sourceItemConfig: CrudFormItem) {
    this.config.items?.forEach(item => {
      if (item.isLinked && item.linkedFieldName === sourceItemConfig.fieldName) {
        const targetItem = this.dataForm.get(item.fieldName);
        const sourceItem = this.dataForm.get(sourceItemConfig.fieldName);
        if (item.linkDataTransformationFunction) {
          targetItem.patchValue(item.linkDataTransformationFunction(sourceItem.value));
        } else {
          targetItem.patchValue(sourceItem.value);
        }
      }
    });
  }

  /* updates isLinked state when click button */
  onLinkChange(item: CrudFormItem) {
    item.isLinked = !item.isLinked;
    this.fieldLinkStateChange.emit(item);
  }

  findLinkedConfigItemByLinkedFieldName(item: CrudFormItem) {
    return this.config.items.find(itemConfig => itemConfig.fieldName === item.linkedFieldName);
  }

  isItemHasLinkedFormItem(item: CrudFormItem) {
    return this.config.items.some(itemConfig => itemConfig.linkedFieldName === item.fieldName);
  }

  unlockAllLinkedFields(parentitem: CrudFormItem) {
    this.config.items?.forEach(item => {
      if (item.isLinked && item.linkedFieldName === parentitem.fieldName) {
        item.isLinked = false;
      }
    });
  }

  // #endregion LINKED FORM ITEMS

  onFieldBlur(fieldName: string) {
    if (this.config?.inputChangeReaction === CrudFormConfigInputChangeReaction.Blur) {
      /* get changed field body */
      const changedFieldBody = this.getChangedFieldValueBody(fieldName);
      /* emit the changed value */
      this.emitFieldValue(changedFieldBody);
    }
  }

  getChangedFieldValueBody(fieldName: string, value?: any) {
    /* get changed field as nested */
    const targetNestedFieldNames = this.getActiveFieldNamesFromActiveBreadcrumbOption(fieldName);
    /* get value from the form if not specified */
    if (!value) {
      /* get field value */
      value = this.dataForm.get(targetNestedFieldNames).value;
    }
    /* define patch data */
    const patchData = {};
    /* set patch data */
    set(patchData, targetNestedFieldNames, value);
    /* return patch data */
    return patchData;
  }

  onFormSubmit() {
    if (this.slideMode) {
      const item = this.visibleFormItems[this.activeSlide];
      if (!this.dataForm.controls[item.fieldName].valid) {
        this.dataForm.controls[item.fieldName].markAsTouched();
        this.dataForm.controls[item.fieldName].markAsDirty();
        this.checkDataFormFieldErrorsByFormItem(item);
        this.dataForm.controls[item.fieldName].updateValueAndValidity({ onlySelf: true });
      }
    }
    let returnData;
    if (this.config.formValueEmitMode === CrudFormValueEmitMode.ReturnAllValues || !this.config.formValueEmitMode) {
      returnData = this.dataForm.value;
    } else if (this.config.formValueEmitMode === CrudFormValueEmitMode.ReturnDirtyValues) {
      returnData = this.formService.getDirtyFormValues(this.dataForm);
    }
    this.submitButtonClick.emit(returnData);
    this.dataForm.markAllAsTouched();
    this.checkAllDataFormFieldErrors();
    this.emitValidity();
    if (this.dataForm.valid) {
      this.emitFormSubmit();
    }
  }

  onFormCancel() {
    this.cancelButtonClick.emit(true);
  }

  emitFormSubmit() {
    this.emitValidity();
    let returnData;
    if (this.config.formValueEmitMode === CrudFormValueEmitMode.ReturnAllValues || !this.config.formValueEmitMode) {
      returnData = this.dataForm.value;
    } else if (this.config.formValueEmitMode === CrudFormValueEmitMode.ReturnDirtyValues) {
      returnData = this.formService.getDirtyFormValues(this.dataForm);
    }
    this.formSubmit.emit(returnData);
  }

  emitFormValue() {
    this.emitValidity();
    let returnData;
    if (this.config.formValueEmitMode === CrudFormValueEmitMode.ReturnAllValues || !this.config.formValueEmitMode) {
      returnData = this.dataForm.value;
    } else if (this.config.formValueEmitMode === CrudFormValueEmitMode.ReturnDirtyValues) {
      returnData = this.formService.getDirtyFormValues(this.dataForm);
    }
    this.formValueChange.emit(returnData);
  }

  emitValidity() {
    if (this.dataForm) {
      this.validityChange.emit(this.dataForm.valid);
    }
  }

  emitFieldValue(value: any) {
    this.fieldValueChange.emit(value);
    this.emitFormValue();
  }

  getActiveFieldNamesFromActiveBreadcrumbOption(fieldName: string) {
    return [
      ...this.objectFieldEditModal.breadcrumbOptions?.map(option => option.data.fieldName) || [],
      fieldName
    ];
  }

  processDataPreview(item: CrudFormObjectItem): string {
    if (item.dataPreview) {
      const nestedFieldNames = this.getActiveFieldNamesFromActiveBreadcrumbOption(item.fieldName);
      const fieldValue = this.dataForm.get(nestedFieldNames).value || null;
      let text = item.dataPreview;
      item.dataPreview.match(/{{\s*[^{]*\s*}}/g).forEach(word => {
        const targetFieldName = word.match(/[^{}\s]+/g).toString();
        text = text.replace(word, fieldValue[targetFieldName] || '').trim();
      });
      return text;
    } else {
      return null;
    }
  }

  getLabelFromConfig(item: CrudFormLabelFunctionItem, data: unknown) {
    return item.functionToDisplay(item, data);
  }

  getDynamicDataForSelectbox(fieldName: string) {
    if (this.dynamicDataForSelectBox?.length) {
      return this.dynamicDataForSelectBox?.find(dynamicData => dynamicData.fieldName === fieldName)?.options;
    }
  }

  /**
   * You can use this function to route component events. ex: (close)="onItemOutputEvent('close', $event)"
   *
   * @param {string} eventName
   * @param {*} eventPayload
   * @memberof RappiderEditFormComponent
   */
  onItemOutputEvent(eventName: string, eventPayload: any) {
    this.itemOutputEvent.emit({ eventName: eventName, eventPayload: eventPayload });
  }

  // #region OBJECT FIELD EDIT MODAL FUNCTIONS

  onObjectFormItemEditClick(item: CrudFormItem) {
    /* open modal if item is in main form items */
    if (this.config.items.includes(item)) {
      this.onObjectFieldEditModalOpen(item);
    } else {
      /* create breadcrumb option */
      const breadcrumbOption = this.createBreadcrumbOptionByObjectItem(
        this.objectFieldEditModal.activeBreadcrumbOption.data.form,
        item
      );
      /* add breadcrumb option */
      this.addBreadcrumbOptionToObjectFieldEditModal(breadcrumbOption);
      /* set active breadcrumb option */
      this.setActiveBreadcrumbOptionOfObjectFieldEditModal(breadcrumbOption);
    }
  }

  showObjectFieldModal() {
    this.objectFieldEditModal.visible = true;
  }

  hideObjectFieldModal() {
    this.objectFieldEditModal.visible = false;
  }

  onObjectFieldEditModalOpen(item: CrudFormItem) {
    /* clear breadcrumb options */
    this.clearBreadcrumbOptionsInObjectFieldEditModal();
    /* show object field edit modal */
    this.showObjectFieldModal();
    /* set object field edit modal by object item ( init ) */
    this.setObjectFieldEditModalByObjectItem(this.dataForm, item);
  }

  createBreadcrumbOptionByObjectItem(form: FormGroup, item: CrudFormObjectItem): BreadcrumbOption {
    return <BreadcrumbOption>{
      label: item.title,
      data: {
        fieldName: item.fieldName,
        items: item.items,
        form: form.get(item.fieldName)
      },
      emittable: true
    };
  }

  setObjectFieldEditModalByObjectItem(form: FormGroup, item: CrudFormItem) {
    /* get breadcrumb option */
    const breadcrumbOption = this.createBreadcrumbOptionByObjectItem(form, item);
    /* add breadcrumb option */
    this.addBreadcrumbOptionToObjectFieldEditModal(breadcrumbOption);
    /* set active breadcrumb option */
    this.setActiveBreadcrumbOptionOfObjectFieldEditModal(breadcrumbOption);
  }

  onObjectFieldEditModalClose() {
    this.hideObjectFieldModal();
    this.resetObjectFieldEditModal();
  }

  onObjectFieldEditModalSubmit() {
    /*
      if breadcrumb is exist, get backward of the selected breadcrumb
      if there is no backward breadcrumb or there is no breadcrumb at all then hide modal
    */
    /* get selected breadcrumb option index */
    const selectedBreadcrumbOptionIndex = this.getSelectedBreadcrumbOptionIndexOfObjectEditModal(
      this.objectFieldEditModal.activeBreadcrumbOption
    );
    /* backward from selected breadcrumb option */
    const breadcrumbOption = this.objectFieldEditModal.breadcrumbOptions[selectedBreadcrumbOptionIndex - 1];
    if (breadcrumbOption) {
      this.onObjectFieldEditModalBreadcrumbOptionClick(breadcrumbOption);
    } else {
      this.hideObjectFieldModal();
    }
  }

  resetObjectFieldEditModal() {
    this.clearBreadcrumbOptionsInObjectFieldEditModal();
    this.objectFieldEditModal.activeBreadcrumbOption = null;
  }

  /* breadcrumb functions */

  clearBreadcrumbOptionsInObjectFieldEditModal() {
    this.objectFieldEditModal.breadcrumbOptions = [];
  }

  addBreadcrumbOptionToObjectFieldEditModal(breadcrumbOption: BreadcrumbOption) {
    this.objectFieldEditModal.breadcrumbOptions.push(breadcrumbOption);
  }

  getActiveBreadcrumbOptionInObjectFieldEditModal() {
    if (this.objectFieldEditModal?.breadcrumbOptions.length) {
      const lastIndex = this.objectFieldEditModal?.breadcrumbOptions.length - 1;
      return this.objectFieldEditModal.breadcrumbOptions[lastIndex];
    } else {
      return null;
    }
  }

  setActiveBreadcrumbOptionOfObjectFieldEditModal(breadcrumbOption: BreadcrumbOption) {
    this.objectFieldEditModal.activeBreadcrumbOption = breadcrumbOption;
  }

  onObjectFieldEditModalBreadcrumbOptionClick(breadcrumbOption: BreadcrumbOption) {
    /* slice unnecessary breadcrumbs and set it */
    const indexOfActiveBreadcrumb = this.objectFieldEditModal.breadcrumbOptions.findIndex(option => option === breadcrumbOption);
    const breadcrumbOptions = this.objectFieldEditModal.breadcrumbOptions.slice(0, indexOfActiveBreadcrumb + 1);
    this.setBreadcrumbOptionsOfObjectFieldEditModal(breadcrumbOptions);
    /* set active breadcrumb option */
    const activeBreadcrumbOption = this.getActiveBreadcrumbOptionInObjectFieldEditModal();
    this.setActiveBreadcrumbOptionOfObjectFieldEditModal(activeBreadcrumbOption);
  }

  setBreadcrumbOptionsOfObjectFieldEditModal(breadcrumbOptions: BreadcrumbOption[]) {
    this.objectFieldEditModal.breadcrumbOptions = breadcrumbOptions;
  }

  getSelectedBreadcrumbOptionIndexOfObjectEditModal(breadcrumbOption: BreadcrumbOption): number {
    const index = this.objectFieldEditModal.breadcrumbOptions?.findIndex(option => option === breadcrumbOption);
    return index !== -1 ? index : null;
  }

  // #endregion

  // #region PUBLIC FUNCTIONS

  public submit() {
    this.onFormSubmit();
  }

  public reset() {
    this.resetForm();
  }

  public getFormValue(): any {
    return this.dataForm.value;
  }

  public getFormValidity(): boolean {
    return this.dataForm.valid;
  }

  // #endregion

  // #region Slide Mode

  slideModeOkButtonClick() {
    const item = this.visibleFormItems[this.activeSlide];
    // validate existing input
    if (this.dataForm.controls[item.fieldName].valid) {
      // if its last slide save form if its not increase activeSlide 1.
      if (this.visibleFormItems?.length - 1 === this.activeSlide) {
        this.onFormSubmit();
      } else {
        this.activeSlide++;
        this.focusToActiveSlideInput();
      }
    } else {
      this.dataForm.controls[item.fieldName].markAsTouched();
      this.dataForm.controls[item.fieldName].markAsDirty();
      /* set error for changed field */
      this.checkDataFormFieldErrorsByFormItem(item);
      this.dataForm.controls[item.fieldName].updateValueAndValidity({ onlySelf: true });
    }
  }

  slideModeBackButtonClick() {
    this.activeSlide--;
    this.focusToActiveSlideInput();
  }


  focusToActiveSlideInput() {
    setTimeout(() => {
      const textboxInputs = this.elRef.nativeElement.querySelectorAll('rappider-textbox input');
      const textareaInputs = this.elRef.nativeElement.querySelectorAll('rappider-textarea textarea');

      if (textboxInputs.length > this.activeSlide) {
        const activeInput = textboxInputs[this.activeSlide];
        if (activeInput) {
          activeInput.focus();
          if (activeInput.value) {
            activeInput.setSelectionRange(activeInput.value.length, activeInput.value.length);
          }
        }
      } else if (textareaInputs.length > 0) {
        const activeTextarea = textareaInputs[0];
        if (activeTextarea) {
          activeTextarea.focus();
          if (activeTextarea.value) {
            activeTextarea.setSelectionRange(activeTextarea.value.length, activeTextarea.value.length);
          }
        }
      }
    }, 0);
  }

  // #endregion Slide Mode

  calculateRows(items: any[]): any[] {
    let currentRowWidth = 0;
    let currentRow = 1;
    const itemsWithRows = items.map(item => {
      if (currentRowWidth + item.columnWidth > 100) {
        currentRow++;
        currentRowWidth = 0;
      }
      currentRowWidth += item.columnWidth;
      return {
        ...item,
        row: currentRow
      };
    });
    return itemsWithRows.map((item) => {
      const countMatchingRows = itemsWithRows.filter(i => i.row === item.row).length;
      return {
        ...item,
        countMatchingRows: countMatchingRows
      };
    });
  }

  getFlexBasis(rowNumberItem: any) {
    const count = rowNumberItem.countMatchingRows;
    const columnWidth = rowNumberItem.columnWidth;
    if (columnWidth && columnWidth !== 100) {
      const marginPerItem = (((count - 1) * 15) / count);
      return `calc(${columnWidth}% - ${marginPerItem}px)`;
    }
    if (count === 1) {
      return '100%';
    }
    if (count > 1) {
      const marginPerItem = (((count - 1) * 15) / count);
      return `calc(${(100 / count).toFixed(2)}% - ${marginPerItem}px)`;
    }
    return '100%';
  }

  onIconClick(fieldName: string) {
    this.iconClick.emit(fieldName);
  }

  transformValidators(form: any): any {
    function removeSurroundingQuotes(str: string) {
      const trimmed = str?.trim();
      if ((trimmed?.startsWith('"') && trimmed?.endsWith('"')) ||
        (trimmed?.startsWith('\'') && trimmed?.endsWith('\''))) {
        return trimmed?.slice(1, -1);
      }
      return trimmed;
    }

    if (!form || !form.items || !Array.isArray(form.items)) {
      return form;
    }

    form.items.forEach((item: any) => {
      if (Array.isArray(item.validators)) {
        item.validators.forEach((validator: any) => {
          if (typeof validator.type === 'string' && validator.type.startsWith('Validators.')) {
            if (validator.type.startsWith('Validators.pattern')) {
              // eslint-disable-next-line no-inner-declarations
              function convertValidator(input: string) {
                const regexMatch = input?.match(/^Validators\.pattern\(\/(.+)\/\)$/);
                if (regexMatch) {
                  return Validators.pattern(new RegExp(regexMatch[1]));
                }
                const strMatch = input?.match(/^Validators\.pattern\((.+)\)$/);
                if (strMatch) {
                  return Validators.pattern(strMatch[1]);
                }
              }
              item.validators[item.validators.indexOf(validator)].type = convertValidator(validator.type);
            } else {
              const validatorName = validator.type.split('(')[0].split('.')[1];

              if (validator.type.includes('(')) {
                const param = validator.type.match(/\(([^)]+)\)/);
                if (param) {
                  if (Validators[validatorName]) {
                    item.validators[item.validators.indexOf(validator)].type = Validators[validatorName](+param[1]);
                  }
                }
              } else {
                if (Validators[validatorName]) {
                  item.validators[item.validators.indexOf(validator)].type = Validators[validatorName];
                }
              }
            }
          } else {
            const fnString = removeSurroundingQuotes(validator.value);
            let updatedValidator;
            try {
              updatedValidator = new Function('return ' + fnString)();
            } catch (error) {
              updatedValidator = new Function('return')();
            }
            item.validators[item.validators.indexOf(validator)].type = updatedValidator;
          }
        });
      }
    });

    return form;
  }

  selectedItemFieldName(fieldName) {
    this.selectedItem = fieldName;
    this.selectItem.emit(fieldName);
  }

  drop(event) {
    const newValue = [...this.value];
    this.swapItemSections(newValue, event.previousIndex, event.currentIndex);
    moveItemInArray(newValue, event.previousIndex, event.currentIndex);
    this.updateItemIndexes(newValue);

    this.value = newValue;
    this.onChange(this.value);
    this.selectItem.emit(null);
    setTimeout(() => this.selectItem.emit(this.selectedItem), 0);
  }

  onDeleteItemClick(deletedItem) {
    this.value = this.value.filter((item, index) => index !== deletedItem.index).map((item, index) => ({
      ...item,
      index: index
    }));
    this.onChange(this.value);
  }

  scrollToBottom() {
    const element = document.getElementById('scroll-to-bottom');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    this.scrollToLastItem = false;
    this.scrollToLastItemEmit.emit(false);
  }

  /**
   * Get fix button text for error
   *
   * @param errorMessage Error message
   * @returns Fix button text
   */
  getFixButtonText(errorMessage: string): string | null {
    if (!errorMessage) {
      return null;
    }

    const fieldName = Object.keys(this.dataFormFieldErrors).find(key =>
      this.dataFormFieldErrors[key]?.includes(errorMessage));

    const field = fieldName ? this.config?.items?.find(item => item.fieldName === fieldName) : null;

    if (field) {
      const validator = field.validators?.find(v =>
        v.errorMessage === errorMessage ||
        (v.errorKey === 'pattern' && v.fixButtonText)
      );
      if (validator?.fixButtonText) {
        return validator.fixButtonText;
      }
    }

    const serviceText = this.formService.getFixButtonTextByErrorType(errorMessage, field);
    // Ensure we return null for empty/falsy values
    return serviceText && serviceText.trim() ? serviceText : null;
  }

  /**
   * Fix button click event.
   *
   * @param fieldName Field to fix
   * @param errors Error message
   */
  onFixButtonClick(fieldName: string, errors: string[]): void {
    if (!errors?.length) {
      return;
    }

    const field = this.config.items.find(item => item.fieldName === fieldName);
    if (!field) {
      return;
    }

    const fixedValue = this.formService.fixAndUpdateFormValue(this.dataForm, fieldName, field);

    if (fixedValue !== null) {
      this.updateFormUI(fieldName, fixedValue, field);
    }
  }

  updateFormUI(fieldName: string, value: any, field: any) {
    this.emitFieldValue(this.getChangedFieldValueBody(fieldName, value));
    this.checkDataFormFieldErrorsByFormItem(field);
    this.cdr.detectChanges();
  }

  /**
   * Process form items and assign indexes
   */
  private processFormItems(): void {
    const hasExistingIndexes = this.config.items.some(item => item.index !== undefined);

    this.config.items = this.config.items.map((item, arrayIndex) => ({
      ...item,
      columnWidth: item?.columnWidth || 100,
      index: hasExistingIndexes
        ? (item.index !== undefined ? item.index : arrayIndex)
        : arrayIndex
    }));

    this.config.items = orderBy(this.calculateRows(this.config.items), 'index', 'asc');
  }

  /**
   * Setup form groups and sections
   */
  private setupFormGroups(): void {
    this.dataForm = this.createFormGroupByCrudFormItems(this.config.items, this.dataForm?.value);

    if (this.config.validators?.length && this.config.items?.length) {
      this.dataForm.setValidators(this.generateFormGroupValidators());
    }

    if (this.config.items.some(item => item.section)) {
      this.grouppedItems = groupBy(this.config.items, 'section');
      this.itemSections = Object.keys(this.grouppedItems).filter(section => section);
    }
  }

  /**
   * Swap item sections during drag and drop
   */
  private swapItemSections(items: any[], prevIndex: number, currIndex: number): void {
    const prevItem = items[prevIndex];
    const currItem = items[currIndex];
    const prevSection = prevItem.section;
    const currSection = currItem.section;

    if (prevSection && currSection) {
      items[prevIndex] = { ...prevItem, section: currSection };
      items[currIndex] = { ...currItem, section: prevSection };
    } else if (prevSection && !currSection) {
      items[currIndex] = { ...currItem, section: prevSection };
      items[prevIndex] = { ...prevItem, section: undefined };
    } else if (!prevSection && currSection) {
      items[prevIndex] = { ...prevItem, section: currSection };
      items[currIndex] = { ...currItem, section: undefined };
    }
  }

  /**
   * Update item indexes to reflect new order
   */
  private updateItemIndexes(items: any[]): void {
    items.forEach((item, index) => {
      items[index] = { ...item, index };
    });
  }
}
